import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

const TREATMENTS = {
  DN: {
    label: "Diabetic Nephropathy",
    color: "#1A5EA8",
    subtitle: "Diabetic kidney disease — protecting the kidney and slowing decline",
    goal: "The goal is to slow progression, since damage already done generally isn't reversible. Glycemic control, blood pressure control, and kidney-protective medications work together to preserve remaining function for as long as possible.",
    lines: [
      {
        line: "First-line kidney-protective therapy",
        regimens: [
          { name: "ACE inhibitor or ARB", detail: "Reduces pressure inside the kidney's filtering units and slows albuminuria progression — foundational therapy whenever protein is present in the urine.", tag: "Standard of care" },
          { name: "SGLT2 inhibitor", detail: "Dapagliflozin or empagliflozin — slows kidney decline and reduces cardiovascular risk independent of blood sugar control.", tag: "Standard of care" },
        ],
      },
      {
        line: "Add-on for persistent albuminuria",
        regimens: [
          { name: "Finerenone", detail: "Non-steroidal mineralocorticoid receptor antagonist — reduces kidney and cardiovascular events on top of ACE/ARB and SGLT2i therapy.", tag: "FIDELIO-DKD / FIGARO-DKD trials" },
          { name: "GLP-1 receptor agonist", detail: "Semaglutide and similar agents — glycemic control plus emerging evidence of cardiorenal benefit.", tag: "Emerging" },
        ],
      },
      {
        line: "Glycemic control",
        regimens: [
          { name: "Individualized A1C target", detail: "Set with your endocrinologist or PCP based on age, hypoglycemia risk, and life expectancy.", tag: "Foundational" },
          { name: "Metformin (if eGFR allows)", detail: "First-line glucose-lowering agent for most patients with adequate kidney function; dose-adjusted or avoided at low eGFR.", tag: "Foundational" },
        ],
      },
    ],
    supportive: [
      "Home blood pressure monitoring, targeting below 130/80 mmHg",
      "Dietary sodium moderation; protein intake guided by a renal dietitian",
      "Statin therapy for cardiovascular risk reduction",
      "Regular diabetic eye and foot exams — shared risk factors with kidney disease",
      "Vaccination review (pneumococcal, hepatitis B) before advanced CKD/dialysis",
      "Potassium-conscious diet once on ACE/ARB or finerenone",
    ],
    monitoring: "eGFR and urine albumin-to-creatinine ratio (UACR) every 3-6 months. Potassium and creatinine checked 1-2 weeks after starting or adjusting ACE inhibitors, ARBs, or finerenone. A1C every 3 months.",
  },
  HN: {
    label: "Hypertensive Nephrosclerosis",
    color: "#0B2545",
    subtitle: "Hypertensive kidney disease — blood pressure is the primary lever",
    goal: "Blood pressure control is the single most important intervention. Getting to target and staying there reduces further arterial damage and slows the pace of nephron loss.",
    lines: [
      {
        line: "First-line blood pressure control",
        regimens: [
          { name: "ACE inhibitor or ARB", detail: "Preferred first-line agent — lowers blood pressure while directly protecting the kidney, especially with any proteinuria present.", tag: "Preferred first-line" },
        ],
      },
      {
        line: "Add-on antihypertensives",
        regimens: [
          { name: "Calcium channel blocker", detail: "Commonly added when target blood pressure isn't reached on an ACE inhibitor or ARB alone.", tag: "Combination often needed" },
          { name: "Thiazide or loop diuretic", detail: "Diuretic choice depends on eGFR — thiazides lose effectiveness at lower eGFR, where loop diuretics take over.", tag: "eGFR-dependent" },
        ],
      },
      {
        line: "Resistant hypertension",
        regimens: [
          { name: "Mineralocorticoid receptor antagonist", detail: "Spironolactone or similar, added when blood pressure remains uncontrolled on 3+ agents.", tag: "Consider secondary causes" },
          { name: "Hypertension specialist referral", detail: "Evaluates for secondary causes (renal artery stenosis, sleep apnea, endocrine causes) when control remains difficult.", tag: "As needed" },
        ],
      },
    ],
    supportive: [
      "Home blood pressure monitoring with a validated cuff",
      "Sodium restriction and weight management",
      "Screening for and treating obstructive sleep apnea if present",
      "Statin therapy for cardiovascular risk reduction",
      "Limit NSAIDs, which can worsen blood pressure and kidney function",
      "Regular eGFR and UACR monitoring to track kidney trajectory",
    ],
    monitoring: "Blood pressure checks and eGFR/UACR every 3-6 months. Potassium and creatinine checked 1-2 weeks after starting or adjusting an ACE inhibitor or ARB.",
  },
  GN: {
    label: "Glomerulonephritis",
    color: "#0F766E",
    subtitle: "Immune-mediated kidney disease — subtype determines treatment",
    goal: "Treatment aims to calm the immune-mediated injury driving the specific subtype, while general kidney-protective measures apply across all subtypes. The right regimen depends entirely on getting an accurate biopsy-confirmed diagnosis first.",
    lines: [
      {
        line: "Immunosuppression (subtype-dependent)",
        regimens: [
          { name: "Corticosteroids", detail: "Often first-line to reduce active inflammation, dosed and tapered based on subtype and severity.", tag: "Subtype-specific" },
          { name: "Mycophenolate mofetil", detail: "Commonly used for lupus nephritis and some forms of FSGS as a steroid-sparing agent.", tag: "Subtype-specific" },
          { name: "Cyclophosphamide", detail: "Reserved for more severe or rapidly progressive disease given its side-effect profile.", tag: "Severe disease" },
          { name: "Rituximab", detail: "Anti-CD20 monoclonal antibody — increasingly used for membranous nephropathy and some lupus nephritis cases.", tag: "Growing evidence base" },
        ],
      },
      {
        line: "Targeted / newer agents",
        regimens: [
          { name: "Complement inhibitors", detail: "Targeted therapies for IgA nephropathy and other complement-mediated glomerular diseases.", tag: "Emerging" },
          { name: "Belimumab", detail: "B-cell targeted therapy approved as an add-on for lupus nephritis.", tag: "FDA approved · Lupus nephritis" },
        ],
      },
      {
        line: "Supportive kidney-protective therapy",
        regimens: [
          { name: "ACE inhibitor or ARB", detail: "Used broadly across GN subtypes to reduce proteinuria and slow progression, alongside subtype-specific treatment.", tag: "Across all subtypes" },
          { name: "SGLT2 inhibitor", detail: "Increasingly used as an add-on across proteinuric kidney diseases, including several GN subtypes.", tag: "Across all subtypes" },
        ],
      },
    ],
    supportive: [
      "Infection precautions while on immunosuppressive therapy",
      "Bone health monitoring (calcium, vitamin D, bone density) on long-term steroids",
      "Vaccination review before starting immunosuppression, when possible",
      "Blood pressure control alongside immune-directed treatment",
      "Blood clot risk awareness with nephrotic-range proteinuria",
      "Regular monitoring for treatment side effects specific to the chosen agent",
    ],
    monitoring: "Proteinuria/UACR trend, kidney function labs every 4-12 weeks during active treatment, and subtype-specific markers (complement levels, autoimmune serologies) as relevant to your diagnosis.",
  },
  PKD: {
    label: "Polycystic Kidney Disease",
    color: "#7C3AED",
    subtitle: "Hereditary — slowing cyst growth and protecting kidney function",
    goal: "The goal is to slow the rate of cyst growth and kidney volume increase, control blood pressure (which often rises early in PKD), and plan ahead given the hereditary nature of the disease.",
    lines: [
      {
        line: "Disease-modifying therapy",
        regimens: [
          { name: "Tolvaptan", detail: "The first FDA-approved therapy shown to slow cyst growth and kidney function decline in rapidly progressing ADPKD. Requires monthly liver function monitoring, especially in the first 18 months.", tag: "FDA approved · TEMPO 3:4 / REPRISE trials" },
        ],
      },
      {
        line: "Blood pressure control",
        regimens: [
          { name: "ACE inhibitor or ARB", detail: "First-line, given how often PKD causes hypertension early — often years before kidney function declines.", tag: "First-line" },
        ],
      },
      {
        line: "Investigational",
        regimens: [
          { name: "mTOR inhibitors", detail: "Studied for cyst growth reduction; results have been mixed and use remains investigational.", tag: "Early phase" },
          { name: "Somatostatin analogs", detail: "Being studied for cyst volume reduction, particularly in patients with significant liver cyst burden.", tag: "Early phase" },
        ],
      },
    ],
    supportive: [
      "Hydration guidance — tolvaptan causes significant fluid loss (aquaresis) that needs planning around",
      "Avoid nephrotoxic medications (NSAIDs) where possible",
      "Screening for intracranial aneurysm if there's a family history of aneurysm or stroke",
      "Genetic counseling for family planning, given the 50% inheritance risk in ADPKD",
      "Regular imaging (ultrasound or MRI) to track kidney and cyst size",
      "Caffeine moderation — theoretical link to cyst growth, though evidence is limited",
    ],
    monitoring: "Liver function tests monthly for the first 18 months on tolvaptan, then periodically. Total kidney volume via MRI periodically to track disease progression. Regular eGFR and UACR monitoring.",
  },
};

export default function TreatmentPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("DN");
  const [expandedLine, setExpandedLine] = useState(0);

  const T = TREATMENTS[activeTab];

  return (
    <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: "#F7F9FC", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,700;1,300;1,700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .regimen-row { transition: all 0.15s ease; }
        .regimen-row:hover { background: linear-gradient(135deg, #1A5EA805 0%, #0B254505 100%) !important; transform: translateX(4px); }
      `}</style>

      {/* Hero */}
      <Box sx={{ 
        px: { xs: 3, md: 8 }, 
        pt: 10, 
        pb: 8, 
        background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('data:image/svg+xml,%3Csvg width=\"20\" height=\"20\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Ccircle cx=\"1\" cy=\"1\" r=\"1\" fill=\"rgba(255,255,255,0.1)\"%3E%3C/circle%3E%3C/svg%3E')",
          opacity: 0.3
        }
      }}>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
            <Box sx={{ width: 20, height: "2px", bgcolor: "#FFFFFF" }} />
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#FFFFFF" }}>
              Treatment
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "2.2rem", md: "3.4rem" }, fontWeight: 700, color: "#FFFFFF", mb: 2, letterSpacing: "-0.02em", lineHeight: 1.05 }}>
            Treatment options
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "1.05rem", fontWeight: 400, color: "rgba(255,255,255,0.95)", maxWidth: 560, lineHeight: 1.75, mb: 3 }}>
            Treatment depends heavily on the underlying cause of your CKD. Identifying the correct etiology is important — diabetic, hypertensive, glomerular, and hereditary causes call for different approaches.
          </Typography>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, px: 3, py: 1.5, border: "1px solid rgba(255,200,0,0.5)", borderRadius: "8px", bgcolor: "rgba(255,200,0,0.2)", backdropFilter: "blur(10px)" }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.78rem", fontWeight: 500, color: "#FFFFFF", lineHeight: 1.6 }}>
              ⚠️ Immunosuppressive therapy for glomerulonephritis is not appropriate for diabetic or hypertensive kidney disease, and vice versa. Confirming the correct etiology — sometimes via kidney biopsy — matters before starting subtype-specific treatment.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Cause selector */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: 4, bgcolor: "#FFFFFF", borderBottom: "1px solid #E5E7EB", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {Object.keys(TREATMENTS).map((key) => {
            const isActive = activeTab === key;
            const c = TREATMENTS[key].color;
            return (
              <Box
                key={key}
                onClick={() => { setActiveTab(key); setExpandedLine(0); }}
                sx={{
                  px: 5, py: 2.5, cursor: "pointer", borderRadius: "8px",
                  border: "2px solid",
                  borderColor: isActive ? c : "#E5E7EB",
                  background: isActive ? `linear-gradient(135deg, ${c}15, ${c}05)` : "#FFFFFF",
                  color: isActive ? c : "#6B7280",
                  fontFamily: "'DM Sans'", fontWeight: isActive ? 600 : 500,
                  fontSize: "0.95rem", transition: "all 0.2s ease",
                  "&:hover": { borderColor: c, background: `linear-gradient(135deg, ${c}10, ${c}03)`, transform: "translateY(-2px)", boxShadow: `0 4px 12px ${c}20` }
                }}
              >
                {TREATMENTS[key].label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: 6, animation: "fadeUp 0.25s ease" }} key={activeTab}>
        {/* Goal box */}
        <Box sx={{ 
          border: "2px solid", 
          borderColor: `${T.color}40`, 
          borderRadius: "12px", 
          background: `linear-gradient(135deg, ${T.color}08, ${T.color}03)`,
          p: 4, 
          mb: 8,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "200px",
            height: "200px",
            background: `radial-gradient(circle, ${T.color}15 0%, transparent 70%)`,
            borderRadius: "50%",
            transform: "translate(50%, -50%)"
          }
        }}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: T.color, mb: 2 }}>
              Treatment goal
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "1rem", fontWeight: 400, color: "#374151", lineHeight: 1.8 }}>
              {T.goal}
            </Typography>
          </Box>
        </Box>

        {/* Treatment lines */}
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#9CA3AF", mb: 4 }}>
          Regimens by line
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 10 }}>
          {T.lines.map((lineGroup, li) => (
            <Box key={li} sx={{ 
              border: "1px solid #E5E7EB", 
              borderRadius: "12px", 
              overflow: "hidden", 
              bgcolor: "#FFFFFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              transition: "all 0.2s ease",
              "&:hover": { boxShadow: `0 4px 16px ${T.color}20` }
            }}>
              {/* Line header */}
              <Box
                onClick={() => setExpandedLine(expandedLine === li ? null : li)}
                sx={{
                  px: 4, py: 3.5, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: expandedLine === li ? `linear-gradient(90deg, ${T.color}08 0%, transparent 100%)` : "#FFFFFF",
                  "&:hover": { background: `linear-gradient(90deg, ${T.color}05 0%, transparent 100%)` }, transition: "background 0.15s",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: expandedLine === li ? T.color : "#D1D5DB", borderRadius: "2px", transition: "all 0.2s" }} />
                  <Typography sx={{ fontFamily: "'DM Sans'", fontWeight: 600, fontSize: "1rem", color: "#1F2937" }}>
                    {lineGroup.line}
                  </Typography>
                </Box>
                <Typography sx={{ color: expandedLine === li ? T.color : "#9CA3AF", fontSize: "1.2rem", transform: expandedLine === li ? "rotate(45deg)" : "none", transition: "all 0.15s" }}>+</Typography>
              </Box>

              {/* Regimens */}
              {expandedLine === li && (
                <Box sx={{ borderTop: "1px solid #F3F4F6", bgcolor: "#FAFBFC" }}>
                  {lineGroup.regimens.map((reg, ri) => (
                    <Box
                      key={ri}
                      className="regimen-row"
                      sx={{
                        px: 4, py: 3.5,
                        borderBottom: ri < lineGroup.regimens.length - 1 ? "1px solid #F3F4F6" : "none",
                        display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 3fr 1fr" }, gap: 3, alignItems: "start",
                      }}
                    >
                      <Typography sx={{ fontFamily: "'DM Sans'", fontWeight: 600, fontSize: "0.95rem", color: "#1F2937" }}>
                        {reg.name}
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Sans'", fontWeight: 400, fontSize: "0.9rem", color: "#6B7280", lineHeight: 1.7 }}>
                        {reg.detail}
                      </Typography>
                      <Box sx={{ display: "inline-flex", alignSelf: "flex-start" }}>
                        <Typography sx={{ 
                          fontFamily: "'DM Sans'", 
                          fontSize: "0.7rem", 
                          fontWeight: 600, 
                          letterSpacing: "0.05em", 
                          px: 2, 
                          py: 0.75, 
                          border: `1.5px solid ${T.color}`, 
                          background: `linear-gradient(135deg, ${T.color}15, ${T.color}05)`,
                          color: T.color, 
                          borderRadius: "6px", 
                          whiteSpace: "nowrap" 
                        }}>
                          {reg.tag}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* Supportive care */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 6, mb: 10 }}>
          <Box sx={{ bgcolor: "#FFFFFF", p: 4, borderRadius: "12px", border: "1px solid #E5E7EB" }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: T.color, mb: 3 }}>
              Supportive care
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {T.supportive.map((item, i) => (
                <Box key={i} sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: T.color, mt: 0.75, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: "'DM Sans'", fontWeight: 400, fontSize: "0.9rem", color: "#374151", lineHeight: 1.7 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ bgcolor: "#FFFFFF", p: 4, borderRadius: "12px", border: "1px solid #E5E7EB" }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: T.color, mb: 3 }}>
              Monitoring response
            </Typography>
            <Box sx={{ 
              border: "1px solid", 
              borderColor: `${T.color}30`,
              borderRadius: "8px", 
              p: 3, 
              background: `linear-gradient(135deg, ${T.color}05, transparent)` 
            }}>
              <Typography sx={{ fontFamily: "'DM Sans'", fontWeight: 400, fontSize: "0.9rem", color: "#374151", lineHeight: 1.8 }}>
                {T.monitoring}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* CTA to calculator */}
        <Box
          onClick={() => navigate("/education/calculator")}
          sx={{
            border: "2px solid #E5E7EB", 
            borderRadius: "12px", 
            p: 5,
            cursor: "pointer", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            background: "linear-gradient(135deg, #EEF2FF 0%, #F7F9FC 100%)",
            position: "relative",
            overflow: "hidden",
            "&:hover": { 
              background: "linear-gradient(135deg, #E0E7FF 0%, #EEF2FF 100%)", 
              borderColor: "#1A5EA8",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.15)"
            },
            transition: "all 0.2s",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "radial-gradient(circle, #1A5EA815 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(40%, -40%)"
            }
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: "1.5rem", fontWeight: 700, color: "#1F2937", mb: 0.5 }}>
              Know your CKD stage
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.95rem", fontWeight: 400, color: "#6B7280" }}>
              Enter your eGFR and urine albumin-to-creatinine ratio (UACR) to calculate your KDIGO stage and risk category.
            </Typography>
          </Box>
          <Typography sx={{ color: "#1A5EA8", fontSize: "1.8rem", flexShrink: 0, position: "relative", zIndex: 1 }}>→</Typography>
        </Box>
      </Box>
    </Box>
  );
}